import type { OperationNodeSpec, GroupSpec, CycleRange } from './types';

// ── Errors ───────────────────────────────────────────────────────────────────

export class GroupNotContiguousError extends Error {
  constructor() {
    super('Group stepIds must be contiguous in creationOrder');
    this.name = 'GroupNotContiguousError';
  }
}

export class OperationAlreadyInGroupError extends Error {
  constructor(
    public readonly operationId: string,
    public readonly existingGroupId: string,
  ) {
    super(`Operation ${operationId} already belongs to group ${existingGroupId}`);
    this.name = 'OperationAlreadyInGroupError';
  }
}

export class CycleRangeOverlapError extends Error {
  constructor() {
    super('Cycle ranges must not overlap');
    this.name = 'CycleRangeOverlapError';
  }
}

export class CycleRangeOutOfBoundsError extends Error {
  constructor() {
    super('Cycle range indices must be within group stepIds bounds');
    this.name = 'CycleRangeOutOfBoundsError';
  }
}

export class InvalidIterationCountError extends Error {
  constructor() {
    super('Cycle iteration count must be >= 2');
    this.name = 'InvalidIterationCountError';
  }
}

// ── Helpers ──────────────────────────────────────────────────────────────────

export function getGroupForOperation(
  operationId: string,
  groups: Record<string, GroupSpec>,
): GroupSpec | null {
  return (
    Object.values(groups).find((g) => g.stepIds.includes(operationId)) ?? null
  );
}

// ── Validators ───────────────────────────────────────────────────────────────

export function validateGroupContiguity(
  stepIds: string[],
  operationNodes: Record<string, OperationNodeSpec>,
): void {
  if (stepIds.length <= 1) return;
console.log(operationNodes)
  const orders = stepIds
    .map((id) => operationNodes[id]?.creationOrder ?? -1)
    .sort((a, b) => a - b);

  for (let i = 1; i < orders.length; i++) {
    if (orders[i] !== orders[i - 1] + 1) {
      // throw new GroupNotContiguousError();
    }
  }
}

export function validateSingleMembership(
  stepIds: string[],
  groups: Record<string, GroupSpec>,
  excludeGroupId?: string,
): void {
  for (const stepId of stepIds) {
    const existing = getGroupForOperation(stepId, groups);
    if (existing && existing.id !== excludeGroupId) {
      throw new OperationAlreadyInGroupError(stepId, existing.id!);
    }
  }
}

export function validateCycleRange(
  range: Pick<CycleRange, 'startIndex' | 'endIndex' | 'iterationCount'>,
  group: GroupSpec,
): void {
  if (
    range.startIndex < 0 ||
    range.endIndex < range.startIndex ||
    range.endIndex >= group.stepIds.length
  ) {
    throw new CycleRangeOutOfBoundsError();
  }
  if (range.iterationCount < 2) {
    throw new InvalidIterationCountError();
  }
}

export function validateNoOverlap(
  cycles: CycleRange[],
  excludeId?: string,
): void {
  const filtered = excludeId
    ? cycles.filter((c) => c.id !== excludeId)
    : cycles;

  const sorted = [...filtered].sort((a, b) => a.startIndex - b.startIndex);

  for (let i = 1; i < sorted.length; i++) {
    if (sorted[i].startIndex <= sorted[i - 1].endIndex) {
      throw new CycleRangeOverlapError();
    }
  }
}
